import {csrfToken} from './api';

/**
 * API client for the attorney matter-dashboard design (see
 * database/migrations/Version202609220005.php's own doc comment and
 * docs/ai/FENZSAFE_MVP_INTEGRATION_HANDOFF.md §1). Wired into AttorneyMattersDashboard.tsx, the
 * one screen that calls claimAttorneyMatter()/listClaimedAttorneyMatters() -- this correction
 * replaces this comment's earlier, now-stale "NOT YET WIRED INTO ANY SCREEN" claim. The backend
 * routes this calls (`AttorneyMatterClaimController`) still exist as prepared source only and have
 * never executed against any database (the migration's `HASHES` are a deliberate placeholder), so
 * end-to-end behavior remains unverified even though the frontend wiring itself is complete -- see
 * AttorneyMattersDashboard.tsx's own doc comment for that distinction. Kept as its own small module
 * rather than added to `fenzsafeBackendAdapter.ts`/`fenzsafeStore.ts` since it doesn't fit either's
 * existing `TeamManagementAdapter`/`FenzSafeAdapter` interface shape -- claiming and listing
 * claimed matters is a distinct concern (an attorney's own organization-scoped view), not a
 * mutation on the customer's own matter.
 */
export class FenzSafeAttorneyMatterClaimError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'FenzSafeAttorneyMatterClaimError';
  }
}

export type ClaimedMatter = {
  claimId: string;
  matterId: string;
  matterName: string;
  matterReference: string;
  shareToken: string;
  packageStatus: 'shared' | 'opened' | 'revoked';
  claimedAt: string;
};

type WireClaimedMatter = {
  claim_id: string;
  matter_id: string;
  matter_name: string;
  matter_reference: string;
  share_token: string;
  package_status: string;
  claimed_at: string;
};

async function request(path: string, method: string): Promise<Record<string, unknown>> {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {Accept: 'application/json', 'X-ProFenz-CSRF': csrfToken()},
  });
  const parsed = (await response.json()) as Record<string, unknown>;
  if (!response.ok) {
    const code = typeof parsed.code === 'string' ? parsed.code : 'fenzsafe_attorney_matter_claim.unknown_error';
    const message = typeof parsed.message === 'string' ? parsed.message : 'The request could not be completed.';
    throw new FenzSafeAttorneyMatterClaimError(code, message);
  }
  return parsed;
}

function packageStatusFromWire(status: string): ClaimedMatter['packageStatus'] {
  return status === 'opened' || status === 'revoked' ? status : 'shared';
}

function fromWire(wire: WireClaimedMatter): ClaimedMatter {
  return {
    claimId: wire.claim_id,
    matterId: wire.matter_id,
    matterName: wire.matter_name,
    matterReference: wire.matter_reference,
    shareToken: wire.share_token,
    packageStatus: packageStatusFromWire(wire.package_status),
    claimedAt: wire.claimed_at,
  };
}

/** Claims a share link (its opaque 64-hex-character token) into the signed-in caller's own organization's matter list. Idempotent on the backend -- calling it again for an already-claimed matter succeeds without duplicating anything. */
export async function claimAttorneyMatter(shareToken: string): Promise<void> {
  await request(`/api/v1/fenzsafe/share/${shareToken}/claim`, 'POST');
}

/** Lists matters the signed-in caller's own organization has claimed -- never another organization's. */
export async function listClaimedAttorneyMatters(): Promise<ClaimedMatter[]> {
  const body = await request('/api/v1/fenzsafe/attorney/matters', 'GET');
  const matters = body.matters;

  return Array.isArray(matters) ? (matters as WireClaimedMatter[]).map(fromWire) : [];
}
